import { SVGIcon } from '@/components_deprecated'
import { cn } from '@/lib/utils'
import { format } from '@/utils'
import { isAfter, setMonth, setYear } from 'date-fns'
import capitalize from 'lodash/capitalize'
import range from 'lodash/range'
import { useState } from 'react'
import { FAIcon } from '../fa-icon'
import { Button } from './button'
import { Popover, PopoverContent, PopoverTrigger } from './popover'

interface Props {
  id?: string
  value: Date

  /**
   * How many years into the future to show in the year select.
   * By default it the current year is in the middle of the list,
   * which means 5 future years.
   *
   * It clamps to a value of min 0 and max 11 as to not remove the selected year
   * from the list. If set to 0 it will try to get up to the current year
   * with a bias towards setting the selected in the middle of the list
   * (5 years ahead).
   */
  futureYears?: number
  /**
   * The earliest year to which one can select, the list will have no years
   * before this.
   */
  earliestYear?: number
  /**
   * Disable click on future months from today
   */
  disableAfter?: boolean
  /**
   * Disable cycling of years on year change
   */
  noYearCycle?: boolean

  onOpenChange?(open: boolean): void
  onChange(value: Date): void
}

export function MonthSelect({
  id,
  value,

  futureYears,
  earliestYear: propEarliestYear,
  disableAfter = false,
  noYearCycle = false,

  onOpenChange,
  onChange,
}: Props) {
  const earliestYear = propEarliestYear ?? new Date().getFullYear() - 5

  const [open, setOpen] = useState(false)
  const [popoverWidth, setPopoverWidth] = useState<number>(200)

  const selectedMonth = value.getMonth()
  const selectedYear = value.getFullYear()

  const selectedMonthString = capitalize(format(value, 'LLLL'))

  const months = range(12).map(month =>
    capitalize(format(new Date(selectedYear, month, 1), 'LLLL'))
  )

  const years = (() => {
    const baseYear = noYearCycle ? new Date().getFullYear() : selectedYear
    const earliest = Math.min(baseYear, earliestYear)
    const latest = earliest + (futureYears ?? 12)

    return range(earliest, latest).slice(0, 12)
  })()

  function updatePopoverWidth(el: HTMLButtonElement | null) {
    if (!!el) setPopoverWidth(el.offsetWidth)
  }

  function monthInFuture(month: number) {
    return isAfter(new Date(selectedYear, month, 1), new Date())
  }

  function monthIsDisabled(month: number) {
    return monthInFuture(month) && disableAfter
  }

  function yearInFuture(year: number) {
    return isAfter(new Date(year, selectedMonth, 1), new Date())
  }

  function yearIsDisabled(year: number) {
    return yearInFuture(year) && disableAfter
  }

  function handleMonthClick(month: number) {
    if (monthIsDisabled(month)) return
    onChange(setMonth(value, month))
  }

  function handleYearClick(year: number) {
    if (yearIsDisabled(year)) return
    onChange(setYear(value, year))
  }

  function handleOpenChange(isOpen: boolean) {
    setOpen(isOpen)
    onOpenChange?.(isOpen)
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          ref={updatePopoverWidth}
          className={cn(
            'w-min! p-0 gap-0',
            'bg-quaternary border-gray-200',
            !open ? 'hover:border-stone-400' : 'hover:border-gray-200',
            open && 'border-b-0 rounded-b-none'
          )}
          id={id}
          role="combobox"
          variant="outline"
          aria-expanded={open}
        >
          <div className="flex items-center h-full px-6 border-r border-r-gray-200">
            <SVGIcon
              className="-translate-y-0.5 mr-2 [&_svg]:size-6!"
              type="light"
              icon="calendar-month"
              fill="gray6"
            />

            <span>{selectedMonthString}</span>
          </div>

          <div className="flex justify-between items-center w-34 h-full px-6 group">
            {selectedYear}

            <FAIcon
              className="ml-4 text-gray-300 group-hover:text-secondary"
              icon={open ? 'chevron-up' : 'chevron-down'}
            />
          </div>
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className={cn('w-full p-0', 'border-t-0 rounded-t-none bg-quaternary')}
        style={{ width: popoverWidth }}
        side="bottom"
        sideOffset={0}
      >
        <div className="grid grid-cols-[1fr_auto] font-medium">
          <div className="flex flex-col gap-4 pl-14 pr-6 py-4 border-r border-r-gray-200">
            {months.map((month, idx) => {
              const isSelected = selectedMonth === idx
              const isDisabled = monthIsDisabled(idx)

              return (
                <div
                  key={month}
                  className={cn(
                    'relative cursor-pointer',
                    isSelected && 'text-secondary',
                    isSelected &&
                      'before:absolute before:-left-4 before:top-1/2 before:translate-y-[calc(-50%-1px)] before:inline-block before:size-1.5 before:bg-secondary before:rounded-full before:align-middle',
                    isDisabled && 'text-gray-300 cursor-default'
                  )}
                  onClick={() => handleMonthClick(idx)}
                >
                  {month}
                </div>
              )
            })}
          </div>

          <div className="flex flex-col gap-4 w-34 px-6 py-4">
            {years.map(year => {
              const isSelected = selectedYear === year
              const isDisabled = yearIsDisabled(year)

              return (
                <div
                  key={year}
                  className={cn(
                    'relative cursor-pointer',
                    isSelected && 'text-secondary',
                    isSelected &&
                      'before:absolute before:-left-4 before:top-1/2 before:translate-y-[calc(-50%-1px)] before:inline-block before:size-1.5 before:bg-secondary before:rounded-full before:align-middle',
                    isDisabled && 'text-gray-300 cursor-default'
                  )}
                  onClick={() => handleYearClick(year)}
                >
                  {year}
                </div>
              )
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
